import { computed, inject, Injectable } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { ReportsService } from '../../Services/reports.service';
import { GetM365LicenseAssignmentReport } from '../../Interfaces/api/reports.interface';
import { LicenseAssignment, SubscriptionOverview } from '../../Models/avyna/m365-license-assignment-report.model';
import { ChartDatum } from '../../Modules/Reports/models/report-dashboard.types';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';

const EXPIRING_WINDOW_DAYS = 30;
const ASSIGNMENT_TYPES: readonly string[] = ['Direct', 'Inherited', 'Both'];

export interface SubscriptionUtilizationBar {
  name: string;
  consumed: number;
  total: number;
}

@Injectable()
export class M365LicenseReportFacade {
  private readonly _resourceState = inject(ResourceStateService);

  private readonly _reportsService = inject(ReportsService);

  private readonly _resource = httpResource<GetM365LicenseAssignmentReport>(() =>
    this._reportsService.m365LicenseAssignment()
  );

  readonly isLoading = computed(() => this._resource.isLoading());
  readonly error = computed(() => this._resource.error());
  readonly data = computed(() => this._resourceState.valueOf(this._resource)?.data ?? null);

  readonly licenseAssignments = computed(() => this.data()?.licenseAssignments ?? []);
  readonly subscriptionOverview = computed(() => this.data()?.subscriptionOverview ?? []);

  readonly totalSeats = computed(() => this.sumSubscriptionField('totalLicenses'));
  readonly consumedSeats = computed(() => this.sumSubscriptionField('consumedUnits'));
  readonly availableSeats = computed(() => this.totalSeats() - this.consumedSeats());
  readonly utilization = computed<number | null>(() =>
    this.totalSeats() > 0 ? this.consumedSeats() / this.totalSeats() : null
  );

  readonly reclaimableRows = computed<LicenseAssignment[]>(() =>
    this.licenseAssignments().filter(assignment => assignment.accountEnabled === false)
  );
  readonly wastedSeats = computed(() => this.reclaimableRows().length);

  readonly assignmentTypeDonutData = computed<ChartDatum[]>(() => this.buildAssignmentTypeDonutData());
  readonly subscriptionUtilizationBars = computed<SubscriptionUtilizationBar[]>(() =>
    this.buildSubscriptionUtilizationBars()
  );
  readonly expiringSubscriptions = computed<SubscriptionOverview[]>(() => this.filterExpiringSubscriptions());

  reload(): void {
    this._resource.reload();
  }

  private sumSubscriptionField(field: 'totalLicenses' | 'consumedUnits'): number {
    return this.subscriptionOverview().reduce((sum, row) => sum + row[field], 0);
  }

  private buildAssignmentTypeDonutData(): ChartDatum[] {
    const assignments = this.licenseAssignments();
    return ASSIGNMENT_TYPES.map(type => ({
      name: type,
      value: assignments.filter(assignment => assignment.assignmentType === type).length,
      key: type,
    }));
  }

  private buildSubscriptionUtilizationBars(): SubscriptionUtilizationBar[] {
    return this.subscriptionOverview().map(row => ({
      name: row.subscriptionName,
      consumed: row.consumedUnits,
      total: row.totalLicenses,
    }));
  }

  private filterExpiringSubscriptions(): SubscriptionOverview[] {
    const horizon = Date.now() + EXPIRING_WINDOW_DAYS * 24 * 60 * 60 * 1000;
    return this.subscriptionOverview().filter(
      row => row.licenseStatus === 'Disabled' || this.endsWithinHorizon(row.endDate, horizon)
    );
  }

  private endsWithinHorizon(endDate: string, horizon: number): boolean {
    const parsed = Date.parse(endDate);
    return !Number.isNaN(parsed) && parsed <= horizon;
  }
}
